import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { PageHeader, Section } from "../../components/Common";
import { toast } from "sonner";
import { formatCurrency } from "../../lib/currency";
import { useDataSync } from "../../lib/realtime";
import { CheckCircle2, XCircle, PauseCircle, PlayCircle } from "lucide-react";

const TABS = [
  { id: "pending", label: "Pending" },
  { id: "approved", label: "Approved" },
  { id: "rejected", label: "Rejected" },
  { id: "suspended", label: "Suspended" },
];

const STATUS_COLORS = {
  pending: "bg-amber-50 text-amber-700",
  approved: "bg-emerald-50 text-emerald-700",
  rejected: "bg-rose-50 text-rose-700",
  suspended: "bg-slate-100 text-slate-600",
  expired: "bg-slate-100 text-slate-500",
};

export default function AdminSubscriptions() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("pending");
  const [actioning, setActioning] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/subscription-requests");
    setItems(data);
  };
  useEffect(() => { load(); }, []);
  useDataSync("subscriptions", () => load());

  const act = async (id, action, reason) => {
    setActioning(id);
    try {
      await api.post(`/admin/subscription-requests/${id}/action`, { action, reason });
      toast.success(`Request ${action}d`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setActioning(null);
    }
  };

  const rows = items.filter((r) => r.status === tab);

  return (
    <div>
      <PageHeader title="Subscription requests" subtitle="Approve or reject subscription requests from users." />
      <div className="flex flex-wrap gap-2 mb-4">
        {TABS.map((t) => {
          const count = items.filter((r) => r.status === t.id).length;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              data-testid={`sub-tab-${t.id}`}
              className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${
                tab === t.id ? "bg-blue-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-blue-300"
              }`}>
              {t.label} <span className={`ml-1 ${tab === t.id ? "text-blue-100" : "text-slate-400"}`}>{count}</span>
            </button>
          );
        })}
      </div>

      {rows.length === 0 ? (
        <div className="text-center py-16 px-6 border border-dashed border-slate-200 rounded-xl bg-white text-slate-500">
          No {tab} requests.
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.id} data-testid={`sub-req-${r.id}`} className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <div className="flex items-center gap-3">
                    <h3 className="font-heading font-bold text-slate-900">{r.user_name}</h3>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_COLORS[r.status]}`}>{r.status}</span>
                  </div>
                  <p className="text-sm text-slate-600 mt-0.5">{r.user_email} {r.mobile && `· ${r.mobile}`}</p>
                  <p className="text-sm text-slate-600 mt-0.5">
                    <span className="font-semibold">{r.plan_name}</span> · {formatCurrency(r.amount, r.currency)} / {r.duration_days} days
                    {r.institute_name && <> · Institute: <span className="font-semibold">{r.institute_name}</span></>}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    Requested {new Date(r.created_at).toLocaleString()}
                    {r.activated_at && <> · Activated {new Date(r.activated_at).toLocaleDateString()}</>}
                    {r.expires_at && <> · Expires {new Date(r.expires_at).toLocaleDateString()}</>}
                  </p>
                  {r.rejection_reason && <p className="text-xs text-rose-600 mt-1">Reason: {r.rejection_reason}</p>}
                </div>
                <div className="flex flex-wrap gap-2">
                  {r.status === "pending" && (
                    <>
                      <button onClick={() => act(r.id, "approve")} disabled={actioning === r.id} data-testid={`approve-${r.id}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-60">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Approve
                      </button>
                      <button onClick={() => { const reason = prompt("Reject reason (optional):") || ""; act(r.id, "reject", reason); }} disabled={actioning === r.id}
                        data-testid={`reject-${r.id}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-rose-600 text-white text-xs font-semibold hover:bg-rose-700 disabled:opacity-60">
                        <XCircle className="h-3.5 w-3.5" /> Reject
                      </button>
                    </>
                  )}
                  {r.status === "approved" && (
                    <button onClick={() => act(r.id, "suspend")} disabled={actioning === r.id} data-testid={`suspend-${r.id}`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-600 text-white text-xs font-semibold hover:bg-slate-700 disabled:opacity-60">
                      <PauseCircle className="h-3.5 w-3.5" /> Suspend
                    </button>
                  )}
                  {r.status === "suspended" && (
                    <button onClick={() => act(r.id, "reactivate")} disabled={actioning === r.id} data-testid={`reactivate-${r.id}`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-60">
                      <PlayCircle className="h-3.5 w-3.5" /> Reactivate
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
